import React from 'react';

import { FileRejection, useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';

export const useFeeScheduleFile = (disabled: boolean) => {
  const [error, setError] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [codes, setCodes] = React.useState<string[]>([]);

  const handleError = (files: FileRejection[]) => {
    const messages = files
      .flatMap((file) => file.errors.flatMap((error) => error.message + ' \n'))
      .join();
    setError(messages);
  };

  const onDrop = React.useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const file = acceptedFiles[0];

      setFileName(file.name);

      // Create a new FileReader instance
      const reader = new FileReader();

      // Use the readAsBinaryString method to read the content of the file
      reader.readAsBinaryString(file);

      reader.onload = (event) => {
        const binaryStr = event?.target?.result;

        // Use XLSX.read to parse the data
        const workbook = XLSX.read(binaryStr, { type: 'binary' });

        // Use XLSX.utils.sheet_to_json to convert the first worksheet to JSON
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const data = XLSX.utils.sheet_to_json(worksheet);

        // Check if column called CPT exists
        const hasCPT = Object.keys(data[0] as object).some(
          (key) => key.toLowerCase() === 'cpt'
        );
        if (!hasCPT) {
          setError('CPT column is missing in the document.');
          return;
        }

        // Get all the CPT codes
        const cptCodes = data.map((row: any) => {
          return typeof row['CPT'] === 'number'
            ? row['CPT'].toString()
            : row['CPT'];
        });
        setCodes(cptCodes);
      };

      handleError(rejectedFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: 150000000,
    accept: {
      'application/vnd.ms-excel': ['.xls'], // Added for Excel .xls files
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ], // Added for Excel .xlsx files
    },
    disabled,
  });

  const reset = () => {
    setCodes([]);
    setFileName('');
    setError('');
  };

  return {
    error,
    getRootProps,
    getInputProps,
    fileName,
    extractedCodes: codes,
    reset,
  };
};
